import { client } from '@api/rest/client';
import MaterialIcon from '@components/common/MaterialIcon';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import {
  type WebScraperCompletedData,
  type WebScraperErrorData,
  WebScraperJobStatus,
  type WebScraperProgressData,
  type WebScraperProgressModalProps,
} from '@conversed/shared/src/central/dto/web-scraper.dto';
import { useWebScraperSocket } from '@hooks/web-scraper/useWebScraperSocket';
import webIcon from '@images/web-icon.svg';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// The backend polls Firecrawl every 15 seconds, so the modal can sit without a
// progress update for that long on a perfectly healthy crawl.
const SCRAPING_SLOW_WARNING_MS = 35 * 1000;

const resolveInitialStatus = (
  requestError: string | null | undefined,
  jobId: string | null,
): WebScraperJobStatus => {
  if (requestError) {
    return WebScraperJobStatus.Failed;
  }

  if (jobId) {
    return WebScraperJobStatus.Queued;
  }

  return WebScraperJobStatus.Initializing;
};

const resolveInitialStatusMessage = (
  status: WebScraperJobStatus,
  t: TFunction,
): string => {
  if (status === WebScraperJobStatus.Initializing) {
    return t('web-scraper.progress.starting-request');
  }

  if (status === WebScraperJobStatus.Queued) {
    return t('web-scraper.progress.initializing');
  }

  return t('web-scraper.progress.scraping-in-progress');
};

export const WebScraperProgressModal: React.FC<
  WebScraperProgressModalProps
> = ({ jobId, url, exclusions, requestError, onClose, onComplete }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<WebScraperJobStatus>(
    resolveInitialStatus(requestError, jobId),
  );
  const [pagesFound, setPagesFound] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isSitemapOnly, setIsSitemapOnly] = useState(false);
  const [statusMessage, setStatusMessage] = useState(
    resolveInitialStatusMessage(status, t),
  );
  const [error, setError] = useState<string | null>(requestError || null);
  // Bumped whenever an unresponsive-timer probe finds the job still alive, so the
  // stall-timer effect re-runs and re-arms instead of failing on socket silence.
  const [probeNonce, setProbeNonce] = useState(0);
  // The socket `completed` event and the unresponsive-timer probe can both resolve
  // a finished job, so onComplete is guarded to run exactly once per modal.
  const hasCompletedRef = useRef(false);
  const completeOnce = useCallback(() => {
    if (hasCompletedRef.current) {
      return;
    }

    hasCompletedRef.current = true;
    onComplete();
  }, [onComplete]);

  // pagesFound and probeNonce are heartbeats: the effect re-runs on each new page
  // event or live probe, which resets the stall timers.
  useEffect(() => {
    // Do not start stall timers once the job has reached a terminal state.
    if (
      status === WebScraperJobStatus.Completed ||
      status === WebScraperJobStatus.Failed
    ) {
      return;
    }

    // Warn only after two server polls could have been missed. The backend reports
    // crawl progress on a 15 second poll, so a shorter window would show this
    // warning on a healthy crawl while it waits for the next update. During the
    // Processing (Excel) phase the finalizing message owns the copy, so this
    // warning must not overwrite it.
    const scrapingTakingLongerTimer = setTimeout(() => {
      if (status === WebScraperJobStatus.Processing) {
        return;
      }

      setStatusMessage(t('web-scraper.progress.scraping-taking longer'));
    }, SCRAPING_SLOW_WARNING_MS);

    // After 60 seconds with no new progress, do not fail blindly - the backend
    // may still be crawling or finalizing the Excel with no socket events in
    // flight. Probe the durable status first and only fail if it is genuinely
    // Failed; a still-running job re-arms this timer via probeNonce.
    const unresponsiveTimer = setTimeout(() => {
      if (!jobId) {
        setStatus(WebScraperJobStatus.Failed);
        setError(t('web-scraper.error.backend-unresponsive'));

        return;
      }

      client.webScraper
        .getJobStatus({ params: { jobId } })
        .then((response) => {
          const job = response.data;

          if (job.status === WebScraperJobStatus.Failed) {
            setStatus(WebScraperJobStatus.Failed);
            setError(job.error ?? t('web-scraper.error.backend-unresponsive'));

            return;
          }

          if (job.status === WebScraperJobStatus.Completed) {
            setStatus(WebScraperJobStatus.Completed);
            setTotalPages(job.total);
            setIsTruncated(Boolean(job.truncated));
            setIsSitemapOnly(Boolean(job.sitemapOnly));
            completeOnce();

            return;
          }

          setStatus(job.status);
          setPagesFound(job.progress);
          setProbeNonce((nonce) => nonce + 1);
        })
        .catch(() => {
          setStatus(WebScraperJobStatus.Failed);
          setError(t('web-scraper.error.backend-unresponsive'));
        });
    }, 60 * 1000);

    return () => {
      clearTimeout(scrapingTakingLongerTimer);
      clearTimeout(unresponsiveTimer);
    };
  }, [t, pagesFound, status, probeNonce, jobId, completeOnce]);

  useEffect(() => {
    if (status === WebScraperJobStatus.Processing) {
      setStatusMessage(t('web-scraper.progress.finalizing'));
    }
  }, [status, t]);

  useEffect(() => {
    if (requestError) {
      setStatus(WebScraperJobStatus.Failed);
      setError(requestError);
    }
  }, [requestError]);

  const handleProgress = useCallback(
    (data: WebScraperProgressData) => {
      if (data.jobId === jobId) {
        setStatus(data.status);
        setPagesFound(data.progress);
      }
    },
    [jobId],
  );

  const handleCompleted = useCallback(
    (data: WebScraperCompletedData) => {
      if (data.jobId === jobId) {
        setStatus(WebScraperJobStatus.Completed);
        setTotalPages(data.totalPages);
        setIsTruncated(Boolean(data.truncated));
        setIsSitemapOnly(Boolean(data.sitemapOnly));
        completeOnce();
      }
    },
    [jobId, completeOnce],
  );

  const handleError = useCallback(
    (data: WebScraperErrorData) => {
      if (data.jobId === jobId) {
        console.error('Scraping error:', data);
        setStatus(WebScraperJobStatus.Failed);
        setError(data.error);
      }
    },
    [jobId],
  );

  const handleConnect = useCallback(() => {
    if (!jobId) {
      return;
    }

    client.webScraper
      .getJobStatus({ params: { jobId } })
      .then((response) => {
        const job = response.data;

        setStatus(job.status);
        setPagesFound(job.progress);

        if (job.total) {
          setTotalPages(job.total);
        }

        setIsTruncated(Boolean(job.truncated));
        setIsSitemapOnly(Boolean(job.sitemapOnly));

        if (job.error) {
          setError(job.error);
        }
      })
      .catch((jobStatusError) => {
        // * A rejected fetch (e.g. a 404 for an already cleaned-up job) is
        // * not fatal - the modal keeps relying on live socket events.
        console.error(
          'Failed to resync web scraper job status:',
          jobStatusError,
        );
      });
  }, [jobId]);

  useWebScraperSocket(
    jobId ? handleProgress : undefined,
    jobId ? handleCompleted : undefined,
    jobId ? handleError : undefined,
    jobId ? handleConnect : undefined,
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const isInProgress =
    status === WebScraperJobStatus.Initializing ||
    status === WebScraperJobStatus.Queued ||
    status === WebScraperJobStatus.Scraping ||
    status === WebScraperJobStatus.Processing;

  return (
    <div className='tailwind-modal-form'>
      <div className='tailwind-modal-body'>
        {/* Icon with status overlay */}
        <div className='flex justify-center mb-3'>
          <div className='relative'>
            <img src={webIcon} className='w-20 h-20' alt='web-scraper' />
            {status === WebScraperJobStatus.Completed && (
              <div className='flex items-center absolute top-0 bottom-auto right-[-5px] bg-green-500 rounded-full p-1 leading-none'>
                <MaterialIcon
                  icon='check'
                  classes={['text-white', 'text-lg', '!leading-none']}
                />
              </div>
            )}
            {status === WebScraperJobStatus.Failed && (
              <div className='flex items-center absolute top-0 bottom-auto right-[-5px] bg-red-500 rounded-full p-1 leading-none'>
                <MaterialIcon
                  icon='close'
                  classes={['text-white', 'text-lg', '!leading-none']}
                />
              </div>
            )}
          </div>
        </div>

        {/* URL */}
        <div className='flex gap-2 justify-center mb-2'>
          <p className='text-sm text-gray-600 text-center'>
            {t('web-scraper.progress.url')}:
          </p>
          <p className='text-sm font-medium text-gray-800 break-words text-center'>
            {url}
          </p>
        </div>

        {/* Excluded URLs — only shown when at least one exclusion was specified */}
        {exclusions && exclusions.length > 0 && (
          <div className='flex flex-col justify-center mb-4 min-w-0 overflow-hidden'>
            <p className='text-tiny text-gray-600 text-center whitespace-nowrap font-medium'>
              {t('web-scraper.progress.excluded-urls')}:
            </p>
            <div className='flex flex-col min-w-0 overflow-hidden'>
              {exclusions.map((exclusionUrl) => (
                <p
                  key={exclusionUrl}
                  title={exclusionUrl}
                  className='text-tiny font-normal text-gray-400 text-center truncate'>
                  {exclusionUrl}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Status content */}
        <div className='min-h-[150px] flex flex-col items-center justify-center'>
          {isInProgress && (
            <div className='text-center'>
              <p className='text-base font-medium text-primary-base mb-0'>
                {statusMessage}
              </p>

              {status !== WebScraperJobStatus.Processing && pagesFound > 0 && (
                <div className='mt-4'>
                  <div className='bg-gray-100 rounded-lg p-4 inline-block'>
                    <p className='text-4xl font-medium text-primary-base mb-1'>
                      {pagesFound}
                    </p>
                    <p className='text-xs text-gray-600'>
                      {t('web-scraper.progress.pages-discovered')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {status === WebScraperJobStatus.Completed && (
            <div className='text-center'>
              <p className='text-base font-medium text-primary-base mb-0'>
                {t('web-scraper.progress.completed')}
              </p>
              <p className='text-tiny text-gray-500 mb-3'>
                {t('web-scraper.progress.completed-message')}
              </p>

              {isSitemapOnly && (
                <p className='text-tiny text-gray-500 mb-3'>
                  {t('web-scraper.progress.completed-sitemap-only')}
                </p>
              )}

              {isTruncated && (
                <p className='text-tiny text-gray-500 mb-3'>
                  {t('web-scraper.progress.completed-truncated')}
                </p>
              )}

              <div className='bg-gray-100 rounded-lg p-4 inline-block'>
                <p className='text-4xl font-medium text-primary-base mb-1'>
                  {totalPages}
                </p>
                <p className='text-xs text-gray-600'>
                  {t('web-scraper.progress.pages-scraped')}
                </p>
              </div>
            </div>
          )}

          {status === WebScraperJobStatus.Failed && (
            <div className='text-center'>
              <p className='text-lg font-medium text-gray-800 mb-4'>
                {t('web-scraper.progress.failed')}
              </p>
              <p className='text-sm text-red-600 break-words px-4'>
                {error || t('web-scraper.progress.error-unknown')}
              </p>
            </div>
          )}
        </div>
      </div>

      {(status === WebScraperJobStatus.Completed ||
        status === WebScraperJobStatus.Failed) && (
        <div className='tailwind-modal-footer'>
          <ButtonT
            type='button'
            styling='primary'
            size='xs'
            rounding='md'
            onClick={handleClose}>
            {t('general.buttons.close')}
          </ButtonT>
        </div>
      )}
    </div>
  );
};
